import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { permissionsSchema, quotasSchema } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { useGroups } from "../hooks.ts";
import { Badge, Button, Card, Dialog, ErrorText, Field, Input, PageHeader, Spinner } from "../components/ui.tsx";
import { LEVEL_LABEL } from "../components/knowledge.tsx";
import { groupPath } from "../lib/admin-nav.ts";
import { CAPABILITIES } from "./GroupPage.tsx";

/** Admin → People & groups → Groups: every group at a glance; each opens its own page. */
export function GroupsPage() {
  const groups = useGroups();
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title="Groups"
        description="Every person belongs to one group. The group sets what their assistant may use, how far what it learns is shared, and their budget."
        actions={<Button variant="primary" onClick={() => setCreating(true)}>New group</Button>}
      />
      {groups.isPending && <div className="grid place-items-center py-16"><Spinner /></div>}
      <ErrorText error={groups.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.data?.map((g) => (
          <Link key={g.id} to={groupPath(g.id)} className="group block">
            <Card className="flex h-full flex-col p-5 transition group-hover:border-[var(--color-accent)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{g.name}</h3>
                    {g.isDefault && <Badge tone="accent">default</Badge>}
                  </div>
                  {g.description && <p className="mt-1 text-sm text-[var(--muted)]">{g.description}</p>}
                </div>
                <span className="shrink-0 text-sm text-[var(--muted)]">
                  {g.members} {g.members === 1 ? "member" : "members"}
                </span>
              </div>
              <dl className="mt-5 grid grid-cols-3 gap-3 text-sm">
                <Stat label="Budget / mo" value={g.quotas.monthlyBudgetUsd === null ? "Unlimited" : `$${g.quotas.monthlyBudgetUsd}`} />
                <Stat label="Storage" value={`${g.quotas.storageGb} GB`} />
                <Stat label="Memory" value={`${(g.quotas.memoryMb / 1024).toFixed(g.quotas.memoryMb % 1024 ? 1 : 0)} GB`} />
              </dl>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {CAPABILITIES.filter((t) => g.permissions[t.key] === true).map((t) => (
                  <Badge key={t.key}>{t.label}</Badge>
                ))}
              </div>
              <div className="mt-4 rounded-lg bg-[var(--panel-2)] px-3 py-2 text-xs text-[var(--muted)]">
                <span className="font-medium text-[var(--text)]">Knowledge sharing:</span> memories {LEVEL_LABEL[g.permissions.promoteMemory].toLowerCase()} · skills{" "}
                {LEVEL_LABEL[g.permissions.publishSkills].toLowerCase()}
              </div>
              <div className="mt-auto pt-4 text-right text-sm text-[var(--color-accent)] group-hover:underline">Open →</div>
            </Card>
          </Link>
        ))}
      </div>
      <NewGroupDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[var(--panel-2)] px-3 py-2">
      <dt className="text-xs text-[var(--muted)]">{label}</dt>
      <dd className="mt-0.5 font-medium">{value}</dd>
    </div>
  );
}

/** Name and description first; access, sharing and budget are set on the new group's page (the defaults until then). */
function NewGroupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const create = useMutation({
    mutationFn: () =>
      unwrap(api.admin.groups.$post({ json: { name: name.trim(), description, quotas: quotasSchema.parse({}), permissions: permissionsSchema.parse({}), isDefault: false } })),
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: ["groups"] });
      setName("");
      setDescription("");
      onClose();
      nav(groupPath(g.id, "access"));
    },
  });
  return (
    <Dialog open={open} onClose={onClose} title="New group">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Name">
          <Input required autoFocus maxLength={64} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales" />
        </Field>
        <Field label="Description (optional)">
          <Input maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <p className="text-xs text-[var(--muted)]">It starts with the standard access, sharing and budget. You can change them on the next page.</p>
        <ErrorText error={create.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending || !name.trim()}>
            Create group
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
